import { useCallback } from 'react';
import toastTrigger from '@ui/toast/toast-trigger';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { createWatermarkSet, updateWatermarkSet } from '@/store/slices/watermarks';
import { selectProjectChannel } from '@/store/slices/project/selectors';
import type { IWatermarkElement, IWatermarkSet } from '@/types/watermarks.types';
import { EToastTypes } from '@ui/toast/toast.types';

interface SaveData {
  name: string;
  elements: IWatermarkElement[];
}

const useSaveWatermarkSet = () => {
  const dispatch = useAppDispatch();
  const channel = useAppSelector(selectProjectChannel);

  const saveWatermarkSet = useCallback(
    async (
      existingSet: IWatermarkSet | null,
      data: SaveData,
      onSuccess?: (saved: IWatermarkSet) => void
    ): Promise<IWatermarkSet | null> => {
      if (!channel) return null;

      try {
        let saved: IWatermarkSet;
        if (existingSet) {
          saved = await dispatch(updateWatermarkSet({ ...existingSet, ...data })).unwrap();
        } else {
          saved = await dispatch(
            createWatermarkSet({
              channel: channel as number,
              name: data.name.trim(),
              elements: data.elements,
            })
          ).unwrap();
        }
        onSuccess?.(saved);
        return saved;
      } catch {
        toastTrigger('Error saving watermark', {
          type: EToastTypes.error,
        });
        return null;
      }
    },
    [dispatch, channel]
  );

  return { saveWatermarkSet };
};

export default useSaveWatermarkSet;
